import React, { useRef, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Check from '@mui/icons-material/Check';
import type { PgProduct, ProductAttribute } from '@/shared/types';
import { Card, CardHeader, CardTitle, CardContent } from '@/ui-kit/card';
import { Button } from '@/ui-kit/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui-kit/collapsible';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui-kit/accordian';
import { renderReferences, formatTimestamp } from '@/shared/formatters';
import { useFullFilterStore } from '@/stores/useFullFilterStore';
import { useNetworkChartStore } from '@/stores/useNetworkChartStore';
import { useAIAssistantStore } from '@/stores/useAIAssistantStore';
import { persistLastProductIdForNodeGraphTutorial } from '@/components/tutorials/tutorialSteps';
import { getAstSelectedValues, isAstValueSelected } from '@/shared/filterAstSelections';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui-kit/dropdown-menu';
import { cn } from '@/lib/utils';
import { buildProductShareUrl } from '@/utils/pendingDashboardDeepLink';
import { toast } from 'sonner';

interface ProductCardModalProps {
  product: PgProduct;
  isOpen: boolean;
  onClose: () => void;
  onCreateNodeGraph?: (productId: number, productData?: PgProduct) => void;
  /** driver.js overlay uses z-index 10000 — use this so the modal stays visible during tours */
  stackAboveDriverTutorial?: boolean;
  /** With body.modal-open, only exempted siblings (e.g. driver.js popovers) remain interactive — use for tutorial demo modals */
  tutorialVisualOnly?: boolean;
}

type SelectedItem = {
  key: ProductAttribute;
  value: string;
};

const ProductCardModal: React.FC<ProductCardModalProps> = ({
  product,
  isOpen,
  onClose,
  onCreateNodeGraph,
  stackAboveDriverTutorial = false,
  tutorialVisualOnly = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [initialFilterItems, setInitialFilterItems] = useState<SelectedItem[]>([]);
  const [refOpen, setRefOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [copiedShareLink, setCopiedShareLink] = useState(false);
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);
  const [openIocMenuKey, setOpenIocMenuKey] = useState<string | null>(null);
  const openIocMenuKeyRef = useRef<string | null>(null);
  openIocMenuKeyRef.current = openIocMenuKey;
  const { filterAst, addFilterClause, removeFilterClause } = useFullFilterStore();
  const { onCreateNodeGraph: storeOnCreateNodeGraph } = useNetworkChartStore();
  
  // Use prop callback if provided, otherwise use store callback
  const handleCreateNodeGraph = onCreateNodeGraph || storeOnCreateNodeGraph;

  const handleCopyShareLink = async () => {
    if (!product.id) {
      toast.error('No shareable link available');
      return;
    }

    try {
      await navigator.clipboard.writeText(buildProductShareUrl(product.id));
      setCopiedShareLink(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopiedShareLink(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const getFilterValues = (key: ProductAttribute) => getAstSelectedValues(filterAst, key);

  useEffect(() => {
    if (isOpen && product.id != null) {
      persistLastProductIdForNodeGraphTutorial(product.id);
    }
  }, [isOpen, product.id]);

  // Get current filter values
  const currentFilterValues = useMemo(() => {
    const values = new Set<string>();
    const attributes: ProductAttribute[] = ['source', 'author', 'category', 'region', 'industry', 'tags', 'indicators'];
    attributes.forEach((attribute) => {
      getFilterValues(attribute).forEach((val) => values.add(`${attribute}:${val}`));
    });
    return values;
  }, [filterAst]);

  // Check if there are changes (items to add or remove)
  const hasChanges = useMemo(() => {
    // Check for items to add (selected but not in current filter)
    const hasItemsToAdd = selectedItems.some(item => {
      return !Array.from(currentFilterValues).some(filterKey => {
        const [filterAttr, filterVal] = filterKey.split(':');
        return filterAttr === item.key && filterVal.toLowerCase() === item.value.toLowerCase();
      });
    });
    
    // Check for items to remove (in initial filter but not selected)
    const hasItemsToRemove = initialFilterItems.some(item => {
      return !selectedItems.some(selected => 
        selected.key === item.key && selected.value.toLowerCase() === item.value.toLowerCase()
      );
    });
    
    return hasItemsToAdd || hasItemsToRemove;
  }, [selectedItems, currentFilterValues, initialFilterItems]);

  const handleItemClick = (key: ProductAttribute, value: string) => {
    setSelectedItems(prev => {
      const exists = prev.some(item => 
        item.key === key && item.value.toLowerCase() === value.toLowerCase()
      );
      if (exists) {
        return prev.filter(item => 
          !(item.key === key && item.value.toLowerCase() === value.toLowerCase())
        );
      } else {
        return [...prev, { key, value }];
      }
    });
  };

  const handleApplyFilter = () => {
    // Remove items that were in the initial filter but are no longer selected
    initialFilterItems.forEach(item => {
      const isStillSelected = selectedItems.some(selected => 
        selected.key === item.key && selected.value.toLowerCase() === item.value.toLowerCase()
      );
      if (!isStillSelected) {
        const filterValues = getFilterValues(item.key);
        const actualValue = filterValues.find(v => v.toLowerCase() === item.value.toLowerCase());
        if (actualValue) {
          removeFilterClause(item.key, actualValue);
        }
      }
    });
    
    // Add items that are selected but not in the current filter
    selectedItems.forEach(item => {
      const isInFilter = Array.from(currentFilterValues).some(filterKey => {
        const [filterAttr, filterVal] = filterKey.split(':');
        return filterAttr === item.key && filterVal.toLowerCase() === item.value.toLowerCase();
      });
      if (!isInFilter) {
        addFilterClause(item.key, item.value);
      }
    });
    
    setSelectedItems([]);
  };

  const isItemSelected = (key: ProductAttribute, value: string) => {
    return selectedItems.some(item => 
      item.key === key && item.value.toLowerCase() === value.toLowerCase()
    );
  };

  const isItemInFilter = (key: ProductAttribute, value: string) => {
    return isAstValueSelected(filterAst, key, value);
  };

  const renderSelectableValue = (
    key: ProductAttribute,
    value: string,
    displayValue?: string
  ) => {
    const selected = isItemSelected(key, value);
    const inFilter = isItemInFilter(key, value);
    
    let className = 'inline-block px-2 py-0.5 mx-1 my-0.5 rounded-full cursor-pointer transition-all text-xs';
    
    if (inFilter && selected) {
      // Green for values already in filter store AND still selected
      className += ' bg-emerald-500 text-white ring-2 ring-emerald-300 dark:ring-emerald-700';
    } else if (inFilter && !selected) {
      // Darker grey for values in filter store but deselected (will be removed)
      className += ' bg-muted text-muted-foreground ring-2 ring-muted-foreground/40';
    } else if (selected) {
      // Primary for newly selected values (not in filter yet)
      className += ' bg-primary text-primary-foreground ring-2 ring-primary/40';
    } else {
      // Default styling (not selected and not in filter)
      className += ' bg-muted text-muted-foreground hover:bg-muted/80 hover:ring-1 hover:ring-primary/50';
    }
    
    return (
      <span
        key={`${key}-${value}`}
        onClick={(e) => {
          e.stopPropagation();
          handleItemClick(key, value);
        }}
        className={className}
      >
        {displayValue || value}
      </span>
    );
  };

  const renderSelectableField = (
    label: string,
    key: ProductAttribute,
    values: string[] | string | undefined,
    showLabel: boolean = true
  ) => {
    if (!values) return null;
    
    const valueArray = Array.isArray(values) ? values : [values];
    if (valueArray.length === 0) return null;

    return (
      <div className="mb-3">
        {showLabel && label && (
          <p className="font-semibold text-foreground mb-1">{label}:</p>
        )}
        <div className="flex flex-wrap items-center">
          {valueArray.map((value) => 
            renderSelectableValue(key, value)
          )}
        </div>
      </div>
    );
  };

  // Initialize selections based on current filter when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialSelections: SelectedItem[] = [];

      // Check each field and pre-select if already in filter (case-insensitive)
      const checkAndAdd = (key: ProductAttribute, values: string[] | string | undefined) => {
        if (!values) return;
        const valueArray = Array.isArray(values) ? values : [values];
        const filterValues = getFilterValues(key);
        
        valueArray.forEach(value => {
          // Check if any filter value matches (case-insensitive)
          const matches = filterValues.some(filterValue => 
            filterValue.toLowerCase() === value.toLowerCase()
          );
          if (matches) {
            // Use the actual filter value (preserve original case) if found
            const actualFilterValue = filterValues.find(filterValue => 
              filterValue.toLowerCase() === value.toLowerCase()
            );
            initialSelections.push({ key, value: actualFilterValue || value });
          }
        });
      };

      checkAndAdd('source', product.source);
      checkAndAdd('author', product.author);
      checkAndAdd('category', product.category);
      checkAndAdd('region', product.region);
      checkAndAdd('industry', product.industry);
      checkAndAdd('tags', product.tags);
      checkAndAdd('indicators', product.indicators ? Object.keys(product.indicators) : undefined);

      setInitialFilterItems(initialSelections);
      setSelectedItems(initialSelections);
    } else {
      setInitialFilterItems([]);
      setSelectedItems([]);
      setOpenIocMenuKey(null);
    }
  }, [isOpen, filterAst, product]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      // Don't close when clicking inside a portaled dropdown (e.g. IOC actions menu)
      // Radix renders content with role="menu"; also check our attribute in case it's forwarded
      const el = event.target as Element;
      if (el.closest?.('[data-modal-dropdown]') || el.closest?.('[role="menu"]')) return;
      // Close modal if click is outside the modal content (Card)
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Prevent ALL pointer events from reaching background when they originate from modal
    // dnd-kit's PointerSensor listens to pointerdown, pointermove, etc.
    // Allow events on dropdown triggers (data-modal-allow-events) and portaled menu content (role="menu") so they work
    const preventPointerEvents = (event: Event) => {
      const target = event.target as Node;
      const el = target as Element;
      if (el.closest?.('[data-modal-allow-events]') || el.closest?.('[data-modal-dropdown]') || el.closest?.('[role="menu"]')) return;
      if (modalRef.current && modalRef.current.contains(target)) {
        const k = openIocMenuKeyRef.current;
        if (k) {
          const inThisIocMenu =
            el.closest?.(`[data-ioc-dropdown-trigger="${k}"]`) ||
            el.closest?.(`[data-ioc-dropdown-content="${k}"]`);
          if (!inThisIocMenu) {
            setOpenIocMenuKey(null);
          }
        }
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    if (isOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';

      if (!tutorialVisualOnly) {
        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('pointerdown', preventPointerEvents, true);
        document.addEventListener('pointermove', preventPointerEvents, true);
        document.addEventListener('pointerup', preventPointerEvents, true);
        document.addEventListener('pointercancel', preventPointerEvents, true);
        document.addEventListener('mousedown', preventPointerEvents, true);
        document.addEventListener('mousemove', preventPointerEvents, true);
        document.addEventListener('mouseup', preventPointerEvents, true);
        document.addEventListener('dragstart', preventPointerEvents, true);
        document.addEventListener('drag', preventPointerEvents, true);
        document.addEventListener('dragend', preventPointerEvents, true);
      }
    }

    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('pointerdown', preventPointerEvents, true);
      document.removeEventListener('pointermove', preventPointerEvents, true);
      document.removeEventListener('pointerup', preventPointerEvents, true);
      document.removeEventListener('pointercancel', preventPointerEvents, true);
      document.removeEventListener('mousedown', preventPointerEvents, true);
      document.removeEventListener('mousemove', preventPointerEvents, true);
      document.removeEventListener('mouseup', preventPointerEvents, true);
      document.removeEventListener('dragstart', preventPointerEvents, true);
      document.removeEventListener('drag', preventPointerEvents, true);
      document.removeEventListener('dragend', preventPointerEvents, true);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, tutorialVisualOnly]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      ref={backdropRef}
      data-modal-backdrop
      {...(tutorialVisualOnly ? { 'data-modal-tutorial-visual-only': '' } : {})}
      className={cn(
        'fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4',
        stackAboveDriverTutorial ? 'z-[10050]' : 'z-[9999]'
      )}
      style={{ pointerEvents: 'none' }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      <Card 
        ref={modalRef} 
        data-modal-content
        className={cn(
          'w-full max-w-4xl max-h-[90vh] overflow-y-auto relative text-sm select-text',
          stackAboveDriverTutorial ? 'z-[10051]' : 'z-[10000]'
        )}
        style={{ pointerEvents: 'auto', userSelect: 'text', cursor: 'text', position: 'relative' }}
        onPointerDown={(e) => {
          e.stopPropagation();
          const nativeEvent = e.nativeEvent;
          if (nativeEvent && typeof (nativeEvent as any).stopImmediatePropagation === 'function') {
            (nativeEvent as any).stopImmediatePropagation();
          }
        }}
        onPointerMove={(e) => {
          e.stopPropagation();
          const nativeEvent = e.nativeEvent;
          if (nativeEvent && typeof (nativeEvent as any).stopImmediatePropagation === 'function') {
            (nativeEvent as any).stopImmediatePropagation();
          }
        }}
        onPointerUp={(e) => {
          e.stopPropagation();
          const nativeEvent = e.nativeEvent;
          if (nativeEvent && typeof (nativeEvent as any).stopImmediatePropagation === 'function') {
            (nativeEvent as any).stopImmediatePropagation();
          }
        }}
        onDragStart={(e) => {
          e.stopPropagation();
          e.preventDefault(); // Prevent drag from starting
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground text-xl font-bold"
          aria-label="Close"
        >
          &times;
        </button>
        <CardHeader>
          <CardTitle className="text-lg text-foreground">{product.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Apply Filter Button and Create Node Graph Button */}
          <div className="mb-4 flex justify-end gap-2">
            {hasChanges && (
              <Button
                onClick={handleApplyFilter}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Apply Filter
              </Button>
            )}
            {handleCreateNodeGraph && (
              <div data-tutorial="product-modal-create-node-graph" className="inline-flex rounded-md">
                <Button
                  onClick={() => {
                    handleCreateNodeGraph(product.id, product);
                    onClose();
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-400 text-white"
                >
                  Create Node Graph
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              {/* Source - Selectable */}
              {product.source && renderSelectableField('Source', 'source', product.source)}
              
              {/* Category - Selectable */}
              {product.category && renderSelectableField('Category', 'category', product.category)}
              
              <p><span className="font-semibold text-foreground">Publication Date:</span> <span className="text-muted-foreground">{formatTimestamp(product.pub_date)}</span></p>
              <p><span className="font-semibold text-foreground">Ingestion Date:</span> <span className="text-muted-foreground">{formatTimestamp(product.timestamp)}</span></p>
            </div>
            <div>
              {/* Authors - Selectable */}
              {product.author && renderSelectableField('Authors', 'author', product.author)}
              
              <p className="flex flex-wrap items-center gap-2">
                <span>
                  <span className="font-semibold text-foreground">Product ID:</span>{' '}
                  <span className="text-muted-foreground">{product.id || 'N/A'}</span>
                </span>
                {product.id ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => void handleCopyShareLink()}
                    aria-label="Copy product link"
                  >
                    {copiedShareLink ? (
                      <Check sx={{ fontSize: 16 }} className="text-muted-foreground" />
                    ) : (
                      <ContentCopy sx={{ fontSize: 16 }} className="text-muted-foreground" />
                    )}
                  </Button>
                ) : null}
              </p>
              <p><span className="font-semibold text-foreground">Type:</span> <span className="text-muted-foreground">{product.type || 'N/A'}</span></p>
            </div>
          </div>
          {product.chainTxn && product.chainTxn.transaction_id && (
            <p>
              <span className="font-semibold text-foreground">Blockchain Transaction: </span>
              <a href={`https://hashscan.io/testnet/transaction/${product.chainTxn.transaction_id}`} 
                target='_blank' 
                rel='noopener noreferrer' 
                className="text-primary hover:text-primary/90 hover:underline">
                {product.chainTxn.transaction_id}
              </a>
            </p>
          )}
          
          {/* Region - Selectable */}
          {product.region && renderSelectableField('Region', 'region', product.region)}
          
          {/* Industry - Selectable */}
          {product.industry && renderSelectableField('Industry', 'industry', product.industry)}
          
          {/* Reference - Collapsible, own box */}
          <Card className="rounded-lg border bg-card text-card-foreground shadow-sm px-4 py-1">
            <Collapsible open={refOpen} onOpenChange={setRefOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 py-1 font-semibold text-foreground hover:underline [&[data-state=open]>svg]:rotate-180">
                <span className="font-semibold">Reference</span>
                <span className="font-normal">
                  ({(() => {
                    const r = product.reference as string | string[] | undefined;
                    if (Array.isArray(r)) return r.length;
                    if (typeof r === 'string') return r.split(',').filter(Boolean).length;
                    return 0;
                  })()})
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="pt-1 text-muted-foreground">{renderReferences(product.reference)}</div>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Tags - Collapsible, Selectable, own box */}
          {product.tags && (
            <Card className="rounded-lg border bg-card text-card-foreground shadow-sm px-4 py-1">
              <Collapsible open={tagsOpen} onOpenChange={setTagsOpen}>
                <CollapsibleTrigger className={cn(
                  'flex items-center gap-2 py-1 font-semibold text-foreground hover:underline [&[data-state=open]>svg]:rotate-180'
                )}>
                  <span className="font-semibold">Tags</span>
                  <span className="font-normal">({product.tags.length})</span>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-1">{renderSelectableField('Tags', 'tags', product.tags, false)}</div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}

          {/* Indicators - Collapsible, own box */}
          {product.indicators && typeof product.indicators === 'object' && (
            <Card className="rounded-lg border bg-card text-card-foreground shadow-sm px-4 py-1">
              <Collapsible open={indicatorsOpen} onOpenChange={setIndicatorsOpen}>
                <CollapsibleTrigger className="flex items-center gap-2 py-1 font-semibold text-foreground hover:underline [&[data-state=open]>svg]:rotate-180">
                  <span className="font-semibold">Indicators</span>
                  <span className="font-normal">
                    ({Object.values(product.indicators).flat().length}) IOCs / ({Object.keys(product.indicators).length}) IOC Types
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <Accordion type="multiple" className="mt-1 w-full">
                    {[...Object.entries(product.indicators)]
                      .sort(([, a], [, b]) => b.length - a.length)
                      .map(([typeName, values]) => (
                      <AccordionItem key={typeName} value={typeName} className="border rounded-lg mb-2 last:mb-0 px-3">
                        <AccordionTrigger className="py-2 text-sm font-semibold text-foreground hover:no-underline [&[data-state=open]>svg]:rotate-180 flex items-center flex-1">
                          <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            {renderSelectableValue('indicators', typeName, typeName.toUpperCase())}
                            <span className="font-normal">({values.length})</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3 pt-0">
                          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                            {values.map((value, idx) => {
                              const iocMenuKey = `${typeName}:${idx}`;
                              return (
                              <li key={idx} className="group flex items-center gap-1">
                                <span
                                  className={cn(
                                    'font-mono text-sm break-all hover:font-bold group-hover:font-bold min-w-0 leading-none',
                                    openIocMenuKey === iocMenuKey && 'underline'
                                  )}
                                >
                                  {value}
                                </span>
                                <DropdownMenu
                                  modal={false}
                                  open={openIocMenuKey === iocMenuKey}
                                  onOpenChange={(next) => setOpenIocMenuKey(next ? iocMenuKey : null)}
                                >
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      data-modal-allow-events
                                      data-ioc-dropdown-trigger={iocMenuKey}
                                      className="shrink-0 p-0.5 rounded hover:bg-muted cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex items-center justify-center"
                                      aria-label="Actions for this IOC"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    data-modal-dropdown
                                    data-ioc-dropdown-content={iocMenuKey}
                                    side="right"
                                    align="start"
                                    className="z-[10001]"
                                  >
                                    <DropdownMenuItem
                                      onClick={() => {
                                        useAIAssistantStore.getState().setPendingSearchQuery(value);
                                        onClose();
                                      }}
                                    >
                                      Send to Search
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        useNetworkChartStore.getState().onOpenNetworkWithIOC?.(value);
                                        onClose();
                                      }}
                                    >
                                      Open a Node Graph
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </li>
                            );
                            })}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Render modal in a portal at document body level to ensure it's above all cards
  return createPortal(modalContent, document.body);
};

export default ProductCardModal;
